import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  TrendingUp,
  Activity,
  ArrowUpRight,
  TrendingDown,
  ArrowRight,
  MoreHorizontal,
  X,
  CheckCircle2,
  RefreshCcw,
  Plus
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';
import DemoSidebar from '../components/DemoSidebar';
import DemoTopBar from '../components/DemoTopBar';

const getDynamicFontSizeStyle = (val: string, baseSizeMobile = 20, baseSizeDesktop = 30) => {
  if (!val || typeof val !== 'string') return {};
  const len = val.length;
  if (len <= 7) return {};
  const reduction = (len - 7) * 0.85;
  const sizeMobile = Math.max(12, baseSizeMobile - reduction);
  const sizeDesktop = Math.max(14, baseSizeDesktop - reduction);
  return {
    fontSize: `clamp(${sizeMobile}px, 2vw, ${sizeDesktop}px)`
  };
};

const DashboardDemo: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pricingCycle, setPricingCycle] = useState<'monthly' | 'yearly'>('monthly');

  // Register Modal States
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [isSubmittingReg, setIsSubmittingReg] = useState(false);
  const [regError, setRegError] = useState('');

  // Mock Action Modals
  const [showApiSyncModal, setShowApiSyncModal] = useState(false);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [isSyncingMock, setIsSyncingMock] = useState(false);
  const [isAddingMock, setIsAddingMock] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(false);

  // Mock Switcher States
  const [mockAccounts, setMockAccounts] = useState<any[]>([
    { id: 'all', type: 'all', name: 'All Accounts' },
    { id: 'binance', type: 'cex', name: 'Binance Demo', exchange: 'Binance' },
    { id: 'bybit', type: 'cex', name: 'Bybit Demo', exchange: 'Bybit' }
  ]);
  const [selectedMockAccount, setSelectedMockAccount] = useState<any>({ id: 'all', type: 'all', name: 'All Accounts' });

  // Mock CEX Form States
  const [cexExchange, setCexExchange] = useState('Binance');
  const [cexApiKey, setCexApiKey] = useState('');
  const [cexSecretKey, setCexSecretKey] = useState('');
  const [cexLabel, setCexLabel] = useState('');

  // Mock DEX Modal & Form States
  const [showDexSyncModal, setShowDexSyncModal] = useState(false);
  const [isSyncingDexMock, setIsSyncingDexMock] = useState(false);
  const [dexNetwork, setDexNetwork] = useState('Ethereum');
  const [dexAddress, setDexAddress] = useState('');
  const [dexLabel, setDexLabel] = useState('');

  // 1-minute timer to redirect to pricing page from demo mode
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/pricing');
    }, 60000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword) {
      setRegError('Please fill in all fields');
      return;
    }
    setIsSubmittingReg(true);
    setRegError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/register-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: regName,
          email: regEmail,
          password: regPassword,
          trader_type: 'day',
          subscription_plan: 'free',
          broker: '',
          sync_method: 'manual'
        })
      });
      const data = await response.json();
      if (response.ok) {
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
          localStorage.setItem('has_cex_accounts', 'false');
        }
        window.location.href = '/dashboard';
      } else {
        setRegError(data.message || 'Registration failed');
      }
    } catch {
      setRegError('Server connection failed');
    } finally {
      setIsSubmittingReg(false);
    }
  };

  const handleApiSyncMockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSyncingMock(true);
    setTimeout(() => {
      setIsSyncingMock(false);

      const newId = `cex_${Date.now()}`;
      const newAcc = { 
        id: newId, 
        type: 'cex', 
        name: cexLabel || `${cexExchange} Custom`, 
        exchange: cexExchange 
      };

      setMockAccounts(prev => [...prev, newAcc]);
      setSelectedMockAccount(newAcc);
      setActionSuccess(true);

      setTimeout(() => {
        setActionSuccess(false);
        setShowApiSyncModal(false);
        // Trigger register popup immediately after exploratory action completion
        setShowRegisterPopup(true);
      }, 1200);
    }, 1500);
  };

  const handleDexSyncMockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSyncingDexMock(true);
    setTimeout(() => {
      setIsSyncingDexMock(false);

      const newId = `dex_${Date.now()}`;
      const newAcc = { 
        id: newId, 
        type: 'dex', 
        name: dexLabel || `Wallet (${dexNetwork})`, 
        network: dexNetwork,
        address: dexAddress 
      };

      setMockAccounts(prev => [...prev, newAcc]);
      setSelectedMockAccount(newAcc);
      setActionSuccess(true);

      setTimeout(() => {
        setActionSuccess(false);
        setShowDexSyncModal(false);
        // Trigger register popup immediately after exploratory action completion
        setShowRegisterPopup(true);
      }, 1200);
    }, 1500);
  };

  const handleAddManualMockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingMock(true);
    setTimeout(() => {
      setIsAddingMock(false);
      setActionSuccess(true);
      setTimeout(() => {
        setActionSuccess(false);
        setShowAddManualModal(false);
        setShowRegisterPopup(true);
      }, 1200);
    }, 1500);
  };

  const [hoveredRadar, setHoveredRadar] = useState<{ x: number; y: number; label: string; value: string } | null>(null);
  const [hoveredEquityPoint, setHoveredEquityPoint] = useState<any | null>(null);

  const monthNames = [
    t('dashboard.january') || "January",
    t('dashboard.february') || "February",
    t('dashboard.march') || "March",
    t('dashboard.april') || "April",
    t('dashboard.may') || "May",
    t('dashboard.june') || "June",
    t('dashboard.july') || "July",
    t('dashboard.august') || "August",
    t('dashboard.september') || "September",
    t('dashboard.october') || "October",
    t('dashboard.november') || "November",
    t('dashboard.december') || "December"
  ];

  const [currentDateForDisplay, setCurrentDateForDisplay] = useState(new Date(2026, 8, 1));

  const displayPortfolioData = React.useMemo(() => {
    const mult = selectedMockAccount.id === 'all' ? 1.0 :
                 selectedMockAccount.id === 'binance' ? 0.65 :
                 selectedMockAccount.id === 'bybit' ? 0.35 :
                 selectedMockAccount.type === 'cex' ? 0.15 : 0.05;

    // Filter active deployments
    const baseDeployments = [
      {
        id: 1,
        asset: "BTC / USDT",
        type: "LONG (20X)",
        entry: "$64,120.45",
        size: "0.45 BTC",
        pnl: "+$842.10",
        percent: "+1.2%",
        duration: "04:12:35",
        positive: true,
        exchange: "BINANCE"
      },
      {
        id: 2,
        asset: "ETH / USDT",
        type: "SHORT (10X)",
        entry: "$2,410.15",
        size: "12.50 ETH",
        pnl: "-$154.20",
        percent: "-0.6%",
        duration: "01:45:12",
        positive: false,
        exchange: "BYBIT"
      }
    ];

    let activeDeployments = [];
    if (selectedMockAccount.id === 'all') {
      activeDeployments = baseDeployments;
    } else if (selectedMockAccount.id === 'binance') {
      activeDeployments = [baseDeployments[0]];
    } else if (selectedMockAccount.id === 'bybit') {
      activeDeployments = [baseDeployments[1]];
    } else if (selectedMockAccount.type === 'cex') {
      activeDeployments = [
        {
          id: 101,
          asset: "SOL / USDT",
          type: "LONG (10X)",
          entry: "$145.20",
          size: "15.0 SOL",
          pnl: "+$42.50",
          percent: "+1.95%",
          duration: "00:15:30",
          positive: true,
          exchange: selectedMockAccount.exchange.toUpperCase()
        }
      ];
    } else if (selectedMockAccount.type === 'dex') {
      activeDeployments = [
        {
          id: 201,
          asset: "UNI / WETH",
          type: "BUY (DEX)",
          entry: "$7.25",
          size: "150 UNI",
          pnl: "+$12.30",
          percent: "+1.13%",
          duration: "02:10:00",
          positive: true,
          exchange: selectedMockAccount.network.toUpperCase()
        }
      ];
    }

    const isSept2026 = currentDateForDisplay.getFullYear() === 2026 && currentDateForDisplay.getMonth() === 8;
    
    if (isSept2026) {
      return {
        totalBalance: (148900 * mult).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) + ".00",
        netPnl: (12482 * mult >= 0 ? "+" : "-") + "$" + Math.abs(12482 * mult).toLocaleString('en-US', { maximumFractionDigits: 0 }),
        netPnlPercent: "14%",
        netPnlPositive: true,
        dayWinRate: "72.1%",
        dayWinRateSub: "31 / 43 DAYS",
        tradeWinRate: "68.4%",
        tradeWinRateSub: "84 / 123 TRADES",
        avgWinLoss: "+$420 / -$185",
        rrRatio: "RR: 2.27",
        performance: [
          { date: "01", realizedValue: 1240 * mult, positive: true, neutral: false, realized: "+$" + Math.round(1240 * mult).toLocaleString(), barLabel: "+$" + Math.round(1240 * mult).toLocaleString(), tradesCount: 4 },
          { date: "02", realizedValue: -420 * mult, positive: false, neutral: false, realized: "-$" + Math.round(420 * mult).toLocaleString(), barLabel: "-$" + Math.round(420 * mult).toLocaleString(), tradesCount: 2 },
          { date: "03", realizedValue: 0, positive: false, neutral: true, realized: "-", barLabel: "-", tradesCount: 0 },
          { date: "04", realizedValue: 890 * mult, positive: true, neutral: false, realized: "+$" + Math.round(890 * mult).toLocaleString(), barLabel: "+$" + Math.round(890 * mult).toLocaleString(), tradesCount: 7 },
          { date: "05", realizedValue: 2100 * mult, positive: true, neutral: false, realized: "+$" + Math.round(2100 * mult).toLocaleString(), barLabel: "+$" + Math.round(2100 * mult).toLocaleString(), tradesCount: 1 },
          { date: "07", realizedValue: -85 * mult, positive: false, neutral: false, realized: "-$" + Math.round(85 * mult).toLocaleString(), barLabel: "-$" + Math.round(85 * mult).toLocaleString(), tradesCount: 1 },
          { date: "09", realizedValue: 4200 * mult, positive: true, neutral: false, realized: "+$" + Math.round(4200 * mult).toLocaleString(), barLabel: "+$" + Math.round(4200 * mult).toLocaleString(), tradesCount: 3 },
          { date: "14", realizedValue: -85 * mult, positive: false, neutral: false, realized: "-$" + Math.round(85 * mult).toLocaleString(), barLabel: "-$" + Math.round(85 * mult).toLocaleString(), tradesCount: 1 }
        ],
        activeDeployments
      };
    }

    const year = currentDateForDisplay.getFullYear();
    const month = currentDateForDisplay.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    
    let seed = (year * 33 + month) * 12345;
    const lcg = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };

    const performance = [];
    let totalRealized = 0;
    let winDays = 0;
    let totalDaysWithTrades = 0;
    let totalTrades = 0;
    let winningTrades = 0;
    let totalWinVal = 0;
    let totalLossVal = 0;
    let winTradesCount = 0;
    let lossTradesCount = 0;

    for (let d = 1; d <= days; d++) {
      const isTradeDay = lcg() > 0.4;
      const dayStr = d.toString().padStart(2, '0');

      if (isTradeDay) {
        const positive = lcg() > 0.45;
        const tradesCount = Math.floor(lcg() * 5) + 1;
        totalTrades += tradesCount;
        totalDaysWithTrades++;
        
        let dayRealized = 0;
        for (let t = 0; t < tradesCount; t++) {
          const tradeVal = Math.round(lcg() * 1500) + 100;
          const isTradeWin = positive ? (lcg() > 0.2) : (lcg() > 0.8);
          if (isTradeWin) {
            dayRealized += tradeVal;
            winningTrades++;
            totalWinVal += tradeVal;
            winTradesCount++;
          } else {
            dayRealized -= tradeVal;
            totalLossVal += tradeVal;
            lossTradesCount++;
          }
        }

        const isPositive = dayRealized > 0;
        if (isPositive) winDays++;

        performance.push({
          date: dayStr,
          realizedValue: dayRealized * mult,
          positive: isPositive,
          neutral: dayRealized === 0,
          realized: (isPositive ? "+" : dayRealized < 0 ? "-" : "") + "$" + Math.abs(Math.round(dayRealized * mult)).toLocaleString('en-US'),
          barLabel: (isPositive ? "+" : dayRealized < 0 ? "-" : "") + "$" + Math.abs(Math.round(dayRealized * mult)).toLocaleString('en-US'),
          tradesCount
        });
        totalRealized += dayRealized;
      } else {
        performance.push({
          date: dayStr,
          realizedValue: 0,
          positive: false,
          neutral: true,
          realized: "-",
          barLabel: "-",
          tradesCount: 0
        });
      }
    }

    const netPnlPositive = totalRealized >= 0;
    const avgWin = winTradesCount > 0 ? Math.round(totalWinVal / winTradesCount) : 0;
    const avgLoss = lossTradesCount > 0 ? Math.round(totalLossVal / lossTradesCount) : 0;

    return {
      totalBalance: (148900 * mult + totalRealized * mult).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) + ".00",
      netPnl: (netPnlPositive ? "+" : "-") + "$" + Math.abs(Math.round(totalRealized * mult)).toLocaleString('en-US'),
      netPnlPercent: Math.round((totalRealized / 136418) * 100) + "%",
      netPnlPositive,
      dayWinRate: totalDaysWithTrades > 0 ? `${Math.round((winDays / totalDaysWithTrades) * 1000) / 10}%` : "0%",
      dayWinRateSub: `${winDays} / ${totalDaysWithTrades} DAYS`,
      tradeWinRate: totalTrades > 0 ? `${Math.round((winningTrades / totalTrades) * 1000) / 10}%` : "0%",
      tradeWinRateSub: `${winningTrades} / ${totalTrades} TRADES`,
      avgWinLoss: `+$${Math.round(avgWin * mult)} / -$${Math.round(avgLoss * mult)}`,
      rrRatio: avgLoss > 0 ? `RR: ${(avgWin / avgLoss).toFixed(2)}` : "RR: 1.00",
      performance,
      activeDeployments
    };
  }, [currentDateForDisplay, selectedMockAccount]);

  const daysInMonth = React.useMemo(() => {
    return new Date(currentDateForDisplay.getFullYear(), currentDateForDisplay.getMonth() + 1, 0).getDate();
  }, [currentDateForDisplay]);

  const startOffset = React.useMemo(() => {
    return new Date(currentDateForDisplay.getFullYear(), currentDateForDisplay.getMonth(), 1).getDay();
  }, [currentDateForDisplay]);

  const balance = displayPortfolioData.totalBalance;
  const pnl = displayPortfolioData.netPnl;
  const pnlPercent = displayPortfolioData.netPnlPercent;
  const pnlPositive = displayPortfolioData.netPnlPositive;

  const dayWinRate = displayPortfolioData.dayWinRate;
  const dayWinRateSub = displayPortfolioData.dayWinRateSub;
  const tradeWinRate = displayPortfolioData.tradeWinRate;
  const tradeWinRateSub = displayPortfolioData.tradeWinRateSub;
  const avgWinLoss = displayPortfolioData.avgWinLoss;
  const rrRatio = displayPortfolioData.rrRatio;

  const equityData = React.useMemo(() => {
    const mult = selectedMockAccount.id === 'all' ? 1.0 :
                 selectedMockAccount.id === 'binance' ? 0.65 :
                 selectedMockAccount.id === 'bybit' ? 0.35 :
                 selectedMockAccount.type === 'cex' ? 0.15 : 0.05;

    const isSept2026 = currentDateForDisplay.getFullYear() === 2026 && currentDateForDisplay.getMonth() === 8;
    
    if (isSept2026) {
      const rawPoints = [115000, 118000, 128000, 125000, 135000, 138000, 148000, 141000, 149000, 150000, 150000].map(v => v * mult);
      const dates = ["START", "TUE 01", "WED 02", "THU 03", "FRI 04", "SAT 05", "SUN 06", "MON 07", "TUE 08", "WED 09", "THU 10"];
      const pnls = ["", "+$1,240.00", "-$420.00", "NO TRADES", "+$890.00", "+$2,100.00", "", "-$85.00", "", "+$4,200.00", ""].map(pnlStr => {
        if (!pnlStr || pnlStr === 'NO TRADES') return pnlStr;
        const val = parseFloat(pnlStr.replace(/[^\d.-]/g, ''));
        if (isNaN(val)) return pnlStr;
        const scaledVal = val * mult;
        return (scaledVal >= 0 ? "+" : "-") + "$" + Math.abs(Math.round(scaledVal)).toLocaleString('en-US');
      });
      return { points: rawPoints, dates, pnls };
    }

    const points = [148900 * mult];
    const dates = ["START"];
    const pnls = [""];
    
    let currentVal = 148900 * mult;
    const sortedPerf = [...displayPortfolioData.performance].sort((a, b) => parseInt(a.date) - parseInt(b.date));
    
    sortedPerf.forEach(day => {
      if (day.realizedValue !== 0) {
        currentVal += day.realizedValue;
        points.push(currentVal);
        const d = new Date(currentDateForDisplay.getFullYear(), currentDateForDisplay.getMonth(), parseInt(day.date));
        const dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
        dates.push(`${dayOfWeek} ${day.date}`);
        pnls.push(day.realized);
      }
    });

    if (points.length === 1) {
      points.push(148900 * mult);
      dates.push("END");
      pnls.push("");
    }

    return { points, dates, pnls };
  }, [currentDateForDisplay, displayPortfolioData, selectedMockAccount]);

  const handleEquityMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pctX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

    const { points, dates, pnls } = equityData;
    const pointsCount = points.length;
    const idx = Math.round(pctX * (pointsCount - 1));
    const clampedIdx = Math.max(0, Math.min(pointsCount - 1, idx));

    const maxVal = Math.max(...points);
    const minVal = Math.min(...points);
    const range = maxVal - minVal || 1000;
    const paddedMin = minVal - range * 0.1;
    const paddedMax = maxVal + range * 0.1;
    const paddedRange = paddedMax - paddedMin;

    const x = (clampedIdx / (pointsCount - 1)) * 100;
    const y = 100 - ((points[clampedIdx] - paddedMin) / paddedRange) * 100;

    const dateStr = dates[clampedIdx] || "";
    const pnlStr = pnls[clampedIdx] || "";
    const positive = !pnlStr.includes("-");

    const fmtBal = points[clampedIdx].toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });

    setHoveredEquityPoint({
      x,
      y,
      balance: fmtBal,
      date: dateStr,
      pnl: pnlStr || undefined,
      positive
    });
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <DemoSidebar 
        onAddCex={() => {
          setCexLabel('');
          setCexApiKey('');
          setCexSecretKey('');
          setCexExchange('Binance');
          setShowApiSyncModal(true);
        }}
        onAddDex={() => {
          setDexLabel('');
          setDexAddress('');
          setDexNetwork('Ethereum');
          setShowDexSyncModal(true);
        }}
      />

      <main className="flex-1 bg-[#050505] min-h-screen pt-16 md:pt-0 overflow-x-hidden">
        <DemoTopBar 
          title="Dashboard Demo" 
          selectedAccount={selectedMockAccount}
          accounts={mockAccounts}
          onAccountChange={(acc) => setSelectedMockAccount(acc)}
          onAddCex={() => {
            setCexLabel('');
            setCexApiKey('');
            setCexSecretKey('');
            setCexExchange('Binance');
            setShowApiSyncModal(true);
          }}
          onAddDex={() => {
            setDexLabel('');
            setDexAddress('');
            setDexNetwork('Ethereum');
            setShowDexSyncModal(true);
          }}
        />

        {/* Demo Mode active preview banner */}
        <div className="mx-auto w-full max-w-[1132px] pt-4 md:pt-8 px-3 md:px-0 font-sans">
          <div className="relative overflow-hidden bg-gradient-to-r from-[#0B0C10] to-[#12131A] border border-[#EF4444]/20 rounded-3xl p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 shadow-[0_0_50px_rgba(229,9,20,0.04)]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/5 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute -left-12 -bottom-12 w-48 h-48 bg-neutral-950 rounded-full blur-[60px] pointer-events-none" />
            
            <div className="flex items-center gap-4 relative z-10 font-sans">
              <div className="w-12 h-12 rounded-2xl bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center justify-center text-[#EF4444] shrink-0 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                <Activity size={22} className="animate-pulse" />
              </div>
              <div>
                <h4 className="text-sm font-bold tracking-wide text-white">
                  {t('demo.bannerTitle') || "Dashboard Demo (Persis Dashboard Utama)"}
                </h4>
                <p className="text-xs text-neutral-400 mt-1 max-w-xl">
                  {t('demo.bannerDesc') || "Tampilan dashboard demo ini 100% persis dengan dashboard utama setelah Anda menghubungkan akun trading atau memasukkan riwayat transaksi Anda."}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 shrink-0 relative z-10">
              <button 
                onClick={() => setShowApiSyncModal(true)}
                className="px-4 py-2.5 rounded-xl bg-[#E50914] hover:bg-[#E50914]/90 text-white text-xs font-bold transition-all shadow-lg shadow-red-950/20 hover:scale-105 cursor-pointer"
              >
                Connect API Sync
              </button>
              <button 
                onClick={() => setShowAddManualModal(true)}
                className="px-4 py-2.5 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-neutral-700 text-white text-xs font-bold transition-all shadow-lg hover:scale-105 cursor-pointer"
              >
                Add Manual Trade
              </button>
            </div>
          </div>
        </div>

        <div className="py-4 md:py-8 flex flex-col items-center w-full px-3 md:px-0 overflow-x-hidden">
          <div className="w-full max-w-[1132px] space-y-8">
            {/* Header */}
            <header className="space-y-1 select-none">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-black tracking-tight">
                    {t('dashboard.title')}
                  </h1>
                  <p className="text-neutral-500 text-sm">
                    {t('dashboard.subtitle')}
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-red-950/20 border border-red-900/30 rounded-full px-4 py-2 select-none">
                  <div className="w-2 h-2 bg-[#E50914] rounded-full animate-pulse" />
                  <span className="text-xs font-bold text-[#E50914]">
                    DEMO SIMULATOR
                  </span>
                </div>
              </div>
            </header>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              <StatCard
                label={t('dashboard.netPnl')}
                value={pnl}
                subValue={pnlPercent}
                positive={pnlPositive}
              />
              <StatCard
                label={t('dashboard.dayWinRate')}
                value={dayWinRate}
                subValue={dayWinRateSub}
              />
              <StatCard
                label={t('dashboard.tradeWinRate')}
                value={tradeWinRate}
                subValue={tradeWinRateSub}
              />
              <StatCard
                label={t('dashboard.avgWinLoss')}
                value={avgWinLoss}
                subValue={rrRatio}
                custom
              />
            </div>

            {/* Charts Row */}
            <div className="flex flex-col lg:flex-row gap-6 font-sans">
              {/* Score Radar Chart */}
              <div className="w-full lg:w-[356px] lg:flex-shrink-0">
                <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 relative overflow-hidden select-none flex flex-col justify-between min-h-[420px] lg:h-[502px] w-full gap-6">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/[0.01] rounded-full blur-[80px] pointer-events-none" />
                  <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest mb-2">{t('dashboard.catatCryptoScore')}</h3>
                  
                  <div className="relative flex-1 flex items-center justify-center max-h-[260px] my-auto">
                    <svg className="w-full h-full max-w-[270px]" viewBox="0 0 270 220">
                      <defs>
                        <filter id="radarNeonGlow" x="-30%" y="-30%" width="160%" height="160%">
                          <feGaussianBlur stdDeviation="5" result="blur" />
                          <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      <circle cx="135" cy="110" r="95" fill="none" stroke="#222330" strokeWidth="1" />
                      <circle cx="135" cy="110" r="65" fill="none" stroke="#1C1E28" strokeWidth="1" strokeDasharray="3 3" />
                      <circle cx="135" cy="110" r="35" fill="none" stroke="#1C1E28" strokeWidth="1" strokeDasharray="3 3" />
                      <line x1="135" y1="15" x2="135" y2="205" stroke="#222330" strokeWidth="1" />
                      <line x1="40" y1="110" x2="230" y2="110" stroke="#222330" strokeWidth="1" />
                      
                      {(() => {
                        const riskScore = 95;
                        const volScore = 82;
                        const sharpeScore = 74;
                        const yieldScore = 88;

                        const scores = [riskScore / 100, volScore / 100, sharpeScore / 100, sharpeScore / 100, yieldScore / 100];
                        const maxR = 95;
                        const cx = 135, cy = 110;
                        const angles = [
                          -Math.PI / 2,
                          -Math.PI / 2 + (2 * Math.PI / 5),
                          -Math.PI / 2 + (4 * Math.PI / 5),
                          -Math.PI / 2 + (6 * Math.PI / 5),
                          -Math.PI / 2 + (8 * Math.PI / 5),
                        ];
                        
                        const vertices = scores.map((s, i) => {
                          const r = Math.max(0.05, s) * maxR;
                          const labelNames = ["Risk", "Vol", "Sharpe", "Sharpe", "Yield"];
                          const scoreVals = [riskScore, volScore, sharpeScore, sharpeScore, yieldScore];
                          return {
                            x: cx + r * Math.cos(angles[i]),
                            y: cy + r * Math.sin(angles[i]),
                            label: `${labelNames[i]} (${scoreVals[i]})`,
                            value: i === 2 || i === 3 
                              ? `${sharpeScore} Sharpe Ratio` 
                              : `${scoreVals[i]}% Score`
                          };
                        });

                        const pts = vertices.map(v => `${v.x},${v.y}`).join(' ');

                        return (
                          <g>
                            <polygon
                              points={pts}
                              fill="rgba(229, 9, 20, 0.15)"
                              stroke="#E50914"
                              strokeWidth="2"
                              filter="url(#radarNeonGlow)"
                            />
                            {vertices.map((v, i) => (
                              <circle
                                key={i}
                                cx={v.x}
                                cy={v.y}
                                r="5"
                                fill="#E50914"
                                stroke="#FFFFFF"
                                strokeWidth="1.2"
                                className="cursor-pointer transition-colors duration-200 hover:fill-white hover:stroke-[#E50914]"
                                style={{ filter: 'drop-shadow(0 0 3px rgba(229, 9, 20, 0.8))' }}
                                onMouseEnter={() => setHoveredRadar({ x: v.x, y: v.y, label: v.label, value: v.value })}
                                onMouseLeave={() => setHoveredRadar(null)}
                              />
                            ))}
                          </g>
                        );
                      })()}
                      <text x="135" y="10" textAnchor="middle" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Risk (95)</text>
                      <text x="238" y="113" textAnchor="start" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Vol (82)</text>
                      <text x="135" y="215" textAnchor="middle" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Sharpe (74)</text>
                      <text x="32" y="113" textAnchor="end" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">Yield (88)</text>
                    </svg>
                    
                    {hoveredRadar && (
                      <div
                        className="absolute bg-neutral-950/95 border border-red-500/30 rounded-xl px-3 py-1.5 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 transition-all duration-150"
                        style={{
                          left: `${(hoveredRadar.x / 270) * 100}%`,
                          top: `${(hoveredRadar.y / 220) * 100}%`,
                          transform: 'translate(-50%, -125%)'
                        }}
                      >
                        <p className="font-bold text-neutral-400 text-[9px] uppercase tracking-wider leading-none">{hoveredRadar.label}</p>
                        <p className="text-xs font-extrabold text-[#E50914] mt-1 leading-none">{hoveredRadar.value}</p>
                      </div>
                    )}

                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
                      <span className="text-5xl font-black text-white tracking-tighter font-sans mt-3">92</span>
                      <span className="text-xs font-bold text-[#E50914] tracking-widest mt-1 uppercase select-none">A+ TIER</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="bg-[#141414] p-4 rounded-2xl text-center select-none">
                      <p className="text-[9px] text-[#777777] uppercase font-semibold tracking-widest mb-1.5 font-sans">{t('dashboard.riskRating')}</p>
                      <p className="text-sm font-extrabold text-white uppercase font-sans tracking-wide">{t('dashboard.excellent')}</p>
                    </div>
                    <div className="bg-[#141414] p-4 rounded-2xl text-center select-none">
                      <p className="text-[9px] text-[#777777] uppercase font-semibold tracking-widest mb-1.5 font-sans">{t('dashboard.consistency')}</p>
                      <p className="text-sm font-extrabold text-white uppercase font-sans tracking-wide">{t('dashboard.top5Percent')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full lg:flex-1 flex flex-col gap-6">
                <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 flex flex-col justify-between w-full min-h-[220px] lg:h-[263px] relative font-sans">
                  <div className="flex justify-between items-start mb-2 select-none">
                    <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">
                      {t('dashboard.cumulativeEquityCurve')}
                    </h3>
                    <div className="text-right">
                      <p className="text-[8px] text-neutral-500 uppercase font-semibold mb-1.5 tracking-wider -translate-y-[4px]">
                        Y-Axis: Total Balance (USD)
                      </p>
                      <p className="text-[11px] font-bold text-white tracking-tight font-sans animate-fade-in" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {balance}
                      </p>
                    </div>
                  </div>
                  
                  <div className="w-full h-[150px] flex relative mt-2 min-h-0">
                    {(() => {
                      const { points, dates } = equityData;
                      const maxVal = Math.max(...points);
                      const minVal = Math.min(...points);
                      const range = maxVal - minVal || 1000;
                      const paddedMin = minVal - range * 0.1;
                      const paddedMax = maxVal + range * 0.1;
                      const paddedRange = paddedMax - paddedMin;

                      const lblMax = paddedMax.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
                      const lblMidMax = (paddedMin + paddedRange * 0.66).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
                      const lblMidMin = (paddedMin + paddedRange * 0.33).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
                      const lblMin = paddedMin.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

                      const dPath = points.map((val, i) => {
                        const x = (i / (points.length - 1)) * 664;
                        const y = 150 - ((val - paddedMin) / paddedRange) * 150;
                        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                      }).join(' ');

                      const polyPoints = `0 150, ${points.map((val, i) => {
                        const x = (i / (points.length - 1)) * 664;
                        const y = 150 - ((val - paddedMin) / paddedRange) * 150;
                        return `${x} ${y}`;
                      }).join(', ')}, 664 150`;

                      return (
                        <div className="w-full h-full flex flex-col justify-between">
                          <div className="w-full flex-1 flex relative min-h-0">
                            <div className="flex flex-col justify-between items-start w-[55px] h-full flex-shrink-0 select-none text-left -translate-y-[4px]">
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{lblMax}</span>
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{lblMidMax}</span>
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{lblMidMin}</span>
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{lblMin}</span>
                            </div>
                            
                            <div 
                              className="relative flex-1 min-h-0"
                              onMouseMove={handleEquityMouseMove}
                              onMouseLeave={() => setHoveredEquityPoint(null)}
                            >
                              <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 664 150">
                                <defs>
                                  <filter id="equityNeonGlow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feGaussianBlur stdDeviation="3" result="blur" />
                                    <feMerge>
                                      <feMergeNode in="blur" />
                                      <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                  </filter>
                                  <linearGradient id="equityGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="rgba(34,197,94,0.45)" />
                                    <stop offset="100%" stopColor="rgba(34,197,94,0.0)" />
                                  </linearGradient>
                                </defs>
                                <polygon points={polyPoints} fill="url(#equityGradient)" />
                                <path d={dPath} fill="none" stroke="#22C55E" strokeWidth="2.5" filter="url(#equityNeonGlow)" />
                              </svg>

                              {hoveredEquityPoint && (
                                <>
                                  <div 
                                    className="absolute top-0 bottom-0 w-px bg-neutral-800/80 pointer-events-none"
                                    style={{ left: `${hoveredEquityPoint.x}%` }}
                                  />
                                  <div 
                                    className="absolute w-2 h-2 rounded-full bg-[#22C55E] border-2 border-white pointer-events-none z-30"
                                    style={{ left: `${hoveredEquityPoint.x}%`, top: `${hoveredEquityPoint.y}%`, transform: 'translate(-50%, -50%)', filter: 'drop-shadow(0 0 4px #22C55E)' }}
                                  />
                                  <div
                                    className="absolute bg-neutral-950/95 border border-neutral-800 rounded-xl p-2.5 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 space-y-1"
                                    style={{
                                      left: `${hoveredEquityPoint.x}%`,
                                      top: `${hoveredEquityPoint.y}%`,
                                      transform: hoveredEquityPoint.x > 75 ? 'translate(-105%, -50%)' : 'translate(15%, -50%)'
                                    }}
                                  >
                                    <p className="font-bold text-[9px] text-[#777777] uppercase tracking-wider leading-none">{hoveredEquityPoint.date}</p>
                                    <p className="text-xs font-black text-white mt-1 leading-none">{hoveredEquityPoint.balance}</p>
                                    {hoveredEquityPoint.pnl && (
                                      <p className={`text-[10px] font-bold ${hoveredEquityPoint.positive ? 'text-[#22C55E]' : 'text-[#EF4444]'} mt-1 leading-none`}>
                                        {hoveredEquityPoint.pnl}
                                      </p>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="w-full flex mt-2 select-none text-center">
                            <div className="w-[55px] flex-shrink-0" />
                            <div className="flex-1 flex justify-between px-1">
                              {dates.map((date, i) => (
                                <span key={i} className="text-[7.5px] font-bold text-neutral-600 font-sans select-none">{date}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 flex flex-col justify-between w-full min-h-[220px] lg:h-[235px] relative font-sans">
                  <div className="flex justify-between items-start mb-2 select-none">
                    <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">{t('dashboard.dailyRealizedPnL')}</h3>
                    <p className="text-[8px] text-neutral-500 uppercase font-semibold tracking-wider">{t('dashboard.last10Days')}</p>
                  </div>
                  
                  <div className="w-full h-[120px] flex relative mt-2 min-h-0">
                    {(() => {
                      const activeDays = displayPortfolioData.performance.filter(d => !d.neutral);
                      const histogramData = activeDays.slice(-10);
                      const maxRealized = Math.max(...histogramData.map(d => Math.abs(d.realizedValue)), 1000);
                      const maxRealizedStr = maxRealized.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

                      return (
                        <div className="w-full h-full flex flex-col justify-between">
                          <div className="w-full flex-1 flex relative min-h-0">
                            <div className="flex flex-col justify-between items-start w-[55px] h-full flex-shrink-0 select-none text-left -translate-y-[4px]">
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">+{maxRealizedStr}</span>
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$0</span>
                              <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">-{maxRealizedStr}</span>
                            </div>
                            
                            <div className="relative flex-1 flex items-end justify-between gap-1.5 h-full px-2 min-h-0">
                              {histogramData.map((day, i) => {
                                return (
                                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative group">
                                    <div className="absolute inset-x-0.5 md:inset-x-1.5 top-0 bottom-0">
                                      <div className="absolute top-1/2 left-0 right-0 h-px bg-neutral-800/80 z-0 pointer-events-none" />
                                      {day.realizedValue > 0 && (
                                        <div 
                                          className="absolute bottom-1/2 left-1/2 -translate-x-1/2 w-[20px] md:w-[32px] rounded-t bg-gradient-to-t from-[#10B981]/40 to-[#10B981] hover:shadow-[0_0_12px_rgba(16,185,129,0.4)] transition-all duration-300"
                                          style={{ height: `${(day.realizedValue / maxRealized) * 46}%` }}
                                        />
                                      )}
                                      {day.realizedValue < 0 && (
                                        <div 
                                          className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[20px] md:w-[32px] rounded-b bg-gradient-to-b from-[#EF4444] to-[#EF4444]/40 hover:shadow-[0_0_12px_rgba(239,68,68,0.4)] transition-all duration-300"
                                          style={{ height: `${(Math.abs(day.realizedValue) / maxRealized) * 46}%` }}
                                        />
                                      )}
                                    </div>

                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-neutral-950/95 border border-neutral-800 rounded-xl p-2.5 text-[10px] text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 w-28 text-center select-none">
                                      <p className="font-bold text-neutral-500 uppercase font-mono text-[8px] leading-none mb-1">
                                        {monthNames[currentDateForDisplay.getMonth()].substring(0, 3)} {day.date}
                                      </p>
                                      <p className={`font-black text-xs leading-none ${day.positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                        {day.realized}
                                      </p>
                                      <p className="text-[8.5px] text-neutral-400 leading-none mt-1">
                                        {day.tradesCount} {day.tradesCount === 1 ? 'Trade' : 'Trades'}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="w-full flex mt-2 select-none text-center">
                            <div className="w-[55px] flex-shrink-0" />
                            <div className="flex-1 flex justify-between px-2">
                              {histogramData.map((day, i) => (
                                <span key={i} className="flex-1 text-[8px] font-bold text-neutral-600 font-sans select-none">{day.date}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-4 md:p-6 flex flex-col justify-between w-full h-auto min-h-[480px] md:min-h-[550px] font-sans mb-6">
              <div className="flex justify-between items-center mb-4 shrink-0 select-none">
                <div>
                  <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">{t('dashboard.performanceCalendar')}</h3>
                  <p className="text-[11px] font-bold text-white tracking-tight font-sans mt-0.5">
                    {monthNames[currentDateForDisplay.getMonth()]} {currentDateForDisplay.getFullYear()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setCurrentDateForDisplay(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                    className="w-8 h-8 rounded-lg bg-neutral-900 border border-neutral-800 flex items-center justify-center text-neutral-600 hover:text-white hover:border-neutral-700 transition-colors cursor-pointer"
                  >
                    <span className="text-xs">&lt;</span>
                  </button>
                  <button 
                    onClick={() => setCurrentDateForDisplay(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                    className="w-8 h-8 rounded-lg bg-neutral-900 border border-neutral-800 flex items-center justify-center text-neutral-600 hover:text-white hover:border-neutral-700 transition-colors cursor-pointer"
                  >
                    <span className="text-xs">&gt;</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1.5 md:gap-2.5 mb-1.5 shrink-0 select-none text-center">
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} className="text-[7.5px] md:text-[9px] font-black uppercase text-neutral-500 tracking-widest">{day}</div>
                ))}
              </div>

              <div className="flex-1 grid grid-cols-7 gap-1.5 md:gap-2.5 min-h-0">
                {Array.from({ length: startOffset }).map((_, idx) => (
                  <CalendarEmpty key={`empty-${idx}`} />
                ))}

                {Array.from({ length: daysInMonth }).map((_, idx) => {
                  const dayNum = idx + 1;
                  const dayData = displayPortfolioData.performance.find(d => parseInt(d.date) === dayNum);
                  const dateStr = `${monthNames[currentDateForDisplay.getMonth()].substring(0, 3)} ${dayNum.toString().padStart(2, '0')}, ${currentDateForDisplay.getFullYear()}`;

                  if (dayData) {
                    return (
                      <CalendarCell
                        key={dayNum}
                        day={dayNum.toString()}
                        value={dayData.neutral ? "-" : dayData.realized}
                        trades={dayData.tradesCount > 0 ? `${dayData.tradesCount} TRADES` : null}
                        positive={dayData.positive}
                        hasTrades={dayData.tradesCount > 0}
                        isNoTrades={dayData.tradesCount === 0 && !dayData.neutral}
                        dateStr={dateStr}
                      />
                    );
                  }

                  return (
                    <CalendarCell
                      key={dayNum}
                      day={dayNum.toString()}
                      value="-"
                      trades={null}
                      positive={false}
                      hasTrades={false}
                      isNoTrades={false}
                      dateStr={dateStr}
                    />
                  );
                })}
              </div>
            </div>

            <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl overflow-hidden flex flex-col justify-between w-full h-auto min-h-[300px] font-sans">
              <div className="p-6 pb-4 shrink-0 select-none">
                <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">{t('dashboard.activeDeployments')}</h3>
              </div>

              <div className="flex-1 overflow-y-auto px-2 md:px-0">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#14151E] text-[9px] uppercase font-bold text-neutral-500 tracking-wider sticky top-0 z-20 border-b border-[#1C1D26]/40 select-none">
                    <tr>
                      <th className="px-8 py-3.5">Asset</th>
                      <th className="px-8 py-3.5">Entry</th>
                      <th className="px-8 py-3.5">Size</th>
                      <th className="px-8 py-3.5">Unrealized PnL</th>
                      <th className="px-8 py-3.5">Duration</th>
                      <th className="px-8 py-3.5 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1C1D26]/40">
                    {displayPortfolioData.activeDeployments.map((dep: any) => (
                      <TableRow
                        key={dep.id}
                        asset={dep.asset}
                        type={dep.type}
                        entry={dep.entry}
                        size={dep.size}
                        pnl={dep.pnl}
                        percent={dep.percent}
                        duration={dep.duration}
                        positive={dep.positive}
                        exchange={dep.exchange}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Email Registration Modal Popup */}
        <AnimatePresence>
          {showRegisterPopup && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-gradient-to-br from-[#0F0F14] to-[#050505] border border-neutral-800 rounded-3xl p-8 max-w-md w-full relative shadow-2xl overflow-hidden font-sans"
              >
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#E50914]/20 rounded-full blur-[80px] pointer-events-none" />
                
                <button 
                  onClick={() => setShowRegisterPopup(false)}
                  className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>

                <div className="flex flex-col items-center text-center space-y-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#E50914] shadow-lg shadow-red-950/40 flex items-center justify-center text-white">
                    <TrendingUp size={22} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white">Get FREE 7 Days PRO</h3>
                    <p className="text-xs text-neutral-400 mt-2 leading-relaxed">
                      Exploration completed! Register now to start your free 7-day Pro trial and track live performance!
                    </p>
                  </div>
                </div>

                <form onSubmit={handleRegisterSubmit} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Name</label>
                    <input 
                      type="text" 
                      placeholder="Enter your name" 
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none transition-colors text-white" 
                      value={regName} 
                      onChange={(e) => setRegName(e.target.value)} 
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Email</label>
                    <input 
                      type="email" 
                      placeholder="Enter your email" 
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none transition-colors text-white" 
                      value={regEmail} 
                      onChange={(e) => setRegEmail(e.target.value)} 
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Password</label>
                    <input 
                      type="password" 
                      placeholder="Create a password" 
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none transition-colors text-white" 
                      value={regPassword} 
                      onChange={(e) => setRegPassword(e.target.value)} 
                      required
                    />
                  </div>

                  {regError && (
                    <div className="p-3 bg-red-900/20 border border-red-900/45 rounded-xl text-[#EF4444] text-xs">
                      {regError}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={isSubmittingReg}
                    className="w-full py-3.5 bg-[#E50914] hover:bg-[#E50914]/90 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isSubmittingReg ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating Account...
                      </>
                    ) : 'Get FREE 7 Days PRO'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Mock API Sync Modal */}
        <AnimatePresence>
          {showApiSyncModal && (
            <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-neutral-950 border border-neutral-800 rounded-3xl p-6 max-w-md w-full relative shadow-2xl font-sans"
              >
                <button 
                  onClick={() => setShowApiSyncModal(false)}
                  className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors cursor-pointer"
                >
                  <X size={20} />
                </button>

                <h3 className="text-lg font-black mb-4">Connect Exchange API (Mock)</h3>
                
                {actionSuccess ? (
                  <div className="py-8 flex flex-col items-center justify-center space-y-3">
                    <CheckCircle2 size={48} className="text-[#10B981] animate-bounce" />
                    <p className="text-sm font-bold text-white">Connection Successful!</p>
                    <p className="text-xs text-neutral-500">Dummy preset portfolio data loaded.</p>
                  </div>
                ) : (
                  <form onSubmit={handleApiSyncMockSubmit} className="space-y-4 text-left">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Exchange</label>
                      <select 
                        value={cexExchange}
                        onChange={(e) => setCexExchange(e.target.value)}
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white"
                      >
                        <option>Binance</option>
                        <option>Bybit</option>
                        <option>OKX</option>
                        <option>Bitget</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Account Label</label>
                      <input 
                        type="text" 
                        placeholder="e.g. My Binance Account" 
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white font-bold" 
                        value={cexLabel}
                        onChange={(e) => setCexLabel(e.target.value)}
                        required 
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">API Key</label>
                      <input 
                        type="text" 
                        placeholder="Enter mock API Key" 
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white font-mono" 
                        value={cexApiKey}
                        onChange={(e) => setCexApiKey(e.target.value)}
                        required 
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Secret Key</label>
                      <input 
                        type="password" 
                        placeholder="Enter mock API Secret" 
                        className="w-full bg-neutral-900 border border-neutral-805 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white font-mono" 
                        value={cexSecretKey}
                        onChange={(e) => setCexSecretKey(e.target.value)}
                        required 
                      />
                    </div>

                    <button 
                      type="submit" 
                      disabled={isSyncingMock}
                      className="w-full py-3.5 bg-[#E50914] hover:bg-[#E50914]/90 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {isSyncingMock ? (
                        <>
                          <RefreshCcw size={16} className="animate-spin" />
                          Testing Mock Sync...
                        </>
                      ) : 'Connect & Import (Mock)'}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Mock Add Manual Modal */}
        <AnimatePresence>
          {showAddManualModal && (
            <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-neutral-950 border border-neutral-800 rounded-3xl p-6 max-w-md w-full relative shadow-2xl font-sans"
              >
                <button 
                  onClick={() => setShowAddManualModal(false)}
                  className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors cursor-pointer"
                >
                  <X size={20} />
                </button>

                <h3 className="text-lg font-black mb-4">Add Manual Trade (Mock)</h3>
                
                {actionSuccess ? (
                  <div className="py-8 flex flex-col items-center justify-center space-y-3">
                    <CheckCircle2 size={48} className="text-[#10B981] animate-bounce" />
                    <p className="text-sm font-bold text-white">Trade Added Successfully!</p>
                  </div>
                ) : (
                  <form onSubmit={handleAddManualMockSubmit} className="space-y-4 text-left">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Symbol</label>
                        <input type="text" placeholder="BTCUSDT" className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white uppercase font-bold" required />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Direction</label>
                        <select className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white">
                          <option>LONG</option>
                          <option>SHORT</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Quantity</label>
                        <input type="number" placeholder="1.0" step="any" className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white text-right font-mono" required />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Entry Price (USD)</label>
                        <input type="number" placeholder="60000" step="any" className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white text-right font-mono" required />
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      disabled={isAddingMock}
                      className="w-full py-3.5 bg-[#E50914] hover:bg-[#E50914]/90 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {isAddingMock ? (
                        <>
                          <RefreshCcw size={16} className="animate-spin" />
                          Saving Mock Trade...
                        </>
                      ) : 'Save Trade (Mock)'}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Mock DEX Wallet Modal */}
        <AnimatePresence>
          {showDexSyncModal && (
            <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-neutral-950 border border-neutral-805 rounded-3xl p-6 max-w-md w-full relative shadow-2xl font-sans"
              >
                <button 
                  onClick={() => setShowDexSyncModal(false)}
                  className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors cursor-pointer"
                >
                  <X size={20} />
                </button>

                <h3 className="text-lg font-black mb-4">Connect DEX Wallet (Mock)</h3>
                
                {actionSuccess ? (
                  <div className="py-8 flex flex-col items-center justify-center space-y-3">
                    <CheckCircle2 size={48} className="text-[#10B981] animate-bounce" />
                    <p className="text-sm font-bold text-white">Connection Successful!</p>
                    <p className="text-xs text-neutral-500">Dummy wallet balance and tokens imported.</p>
                  </div>
                ) : (
                  <form onSubmit={handleDexSyncMockSubmit} className="space-y-4 text-left">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Network</label>
                      <select 
                        value={dexNetwork}
                        onChange={(e) => setDexNetwork(e.target.value)}
                        className="w-full bg-neutral-900 border border-neutral-808 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white"
                      >
                        <option>Ethereum</option>
                        <option>Arbitrum</option>
                        <option>Solana</option>
                        <option>BSC</option>
                        <option>Polygon</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Account Label</label>
                      <input 
                        type="text" 
                        placeholder="e.g. My Metamask Wallet" 
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white font-bold" 
                        value={dexLabel}
                        onChange={(e) => setDexLabel(e.target.value)}
                        required 
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">Wallet Address</label>
                      <input 
                        type="text" 
                        placeholder="0x... or Solana Address" 
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-3 px-4 text-sm focus:border-[#E50914] outline-none text-white font-mono" 
                        value={dexAddress}
                        onChange={(e) => setDexAddress(e.target.value)}
                        required 
                      />
                    </div>

                    <button 
                      type="submit" 
                      disabled={isSyncingDexMock}
                      className="w-full py-3.5 bg-[#E50914] hover:bg-[#E50914]/90 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {isSyncingDexMock ? (
                        <>
                          <RefreshCcw size={16} className="animate-spin" />
                          Connecting Mock Wallet...
                        </>
                      ) : 'Connect Wallet (Mock)'}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      </main>
    </div>
  );
};

/* ======================================================== */
/* INTERNAL SUB-COMPONENTS                                  */
/* ======================================================== */

const StatCard = ({ label, value, subValue, positive = false, custom = false }: any) => {
  const isNeutral = subValue?.includes("0/") || value === "$0 / $0" || value === "0%";
  return (
    <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-4 md:p-6 select-none font-sans relative overflow-hidden flex flex-col justify-between h-[105px] md:h-[135px]">
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/[0.01] rounded-full blur-[40px] pointer-events-none" />
      <p className="text-[9px] md:text-[10px] uppercase font-semibold text-neutral-500 tracking-widest leading-none mb-1 md:mb-2">{label}</p>
      <div className="my-auto flex flex-col justify-center">
        <h2 className="text-lg md:text-2.5xl font-black text-white tracking-tight leading-tight" style={getDynamicFontSizeStyle(value, 18, 26)}>{value}</h2>
      </div>
      <div className="mt-auto flex items-center justify-between">
        {custom ? (
          <span className="text-[10px] md:text-xs font-bold text-neutral-400">{subValue}</span>
        ) : (
          <span className={`text-[10px] md:text-xs font-bold flex items-center gap-1 leading-none ${
            isNeutral ? 'text-neutral-500' : positive ? 'text-[#10B981]' : 'text-[#EF4444]'
          }`}>
            {!isNeutral && (positive ? <ArrowUpRight size={12} /> : <TrendingDown size={12} className="rotate-90" />)}
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
};

const CalendarCell = ({ day, value, trades, positive, hasTrades, isNoTrades, dateStr }: any) => {
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: parseInt(day) * 0.005 }}
      className={`relative group p-1.5 md:p-2.5 flex flex-col justify-between aspect-square md:aspect-auto min-h-[46px] md:min-h-[70px] rounded-xl border select-none transition-all duration-300 ${
        hasTrades 
          ? positive 
            ? 'bg-[#10B981]/5 hover:bg-[#10B981]/10 border-[#10B981]/25 hover:border-[#10B981]/50 shadow-[0_0_15px_rgba(16,185,129,0.02)]' 
            : 'bg-[#EF4444]/5 hover:bg-[#EF4444]/10 border-[#EF4444]/25 hover:border-[#EF4444]/50 shadow-[0_0_15px_rgba(239,68,68,0.02)]'
          : 'bg-[#14151E]/20 hover:bg-[#1C1D26]/30 border-[#1C1D26]/40 hover:border-neutral-800'
      }`}
    >
      <div className="flex justify-between items-center w-full">
        <span className="text-[9px] md:text-[11px] font-black text-neutral-500 font-mono leading-none">{day}</span>
        {hasTrades && (
          <span 
            className={`hidden md:inline-block text-[6.5px] font-extrabold px-1 py-0.5 rounded uppercase tracking-wider font-sans leading-none ${
              !positive 
                ? 'bg-[#EF4444]/15 border border-[#EF4444]/30 text-[#EF4444]' 
                : 'bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981]'
            }`}
          >
            {trades}
          </span>
        )}
        {hasTrades && (
          <span className={`inline-block md:hidden w-1.5 h-1.5 rounded-full ${positive ? 'bg-[#10B981]' : 'bg-[#EF4444]'}`} />
        )}
      </div>
      
      <div className="mt-auto w-full flex flex-col justify-end">
        {hasTrades ? (
          <>
            <p className={`text-[8.5px] md:text-[11.5px] font-black tracking-tight font-sans leading-none mb-1 ${
              positive ? 'text-[#10B981]' : 'text-[#EF4444]'
            }`}>
              {value}
            </p>
            <div className="hidden md:block w-full h-[2.5px] bg-[#1C1D26]/50 rounded-full overflow-hidden relative">
              <div 
                className={`h-full rounded-full ${positive ? 'bg-[#10B981]' : 'bg-[#EF4444]'}`}
                style={{ 
                  width: positive ? '70%' : '35%',
                }}
              />
            </div>
          </>
        ) : (
          <p className="text-[9px] md:text-[10px] font-bold tracking-tight font-sans text-neutral-800 leading-none">-</p>
        )}
      </div>

      {hasTrades && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-neutral-950/95 border border-neutral-800 rounded-xl p-2.5 text-[10px] text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 w-36 text-center select-none">
          <p className="font-bold text-neutral-500 uppercase font-mono text-[8px] leading-none mb-1">{dateStr}</p>
          <p className={`font-black text-xs leading-none ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
            {value}
          </p>
          <p className="text-[8px] text-neutral-400 leading-none mt-1.5 uppercase tracking-wider font-semibold font-mono">
            {trades}
          </p>
        </div>
      )}
    </motion.div>
  );
};

const CalendarEmpty = () => (
  <div className="bg-[#0A0A0A]/50 border border-[#1C1D26]/10 rounded-xl aspect-square md:aspect-auto min-h-[46px] md:min-h-[70px]"></div>
);

const TableRow = ({ asset, type, entry, size, pnl, percent, duration, positive = false, exchange }: any) => {
  const [imgError, setImgError] = useState(false);
  const displayType = type || "";
  const isLong = displayType.toUpperCase().includes("LONG") || displayType.toUpperCase().includes("BUY");
  const isShort = displayType.toUpperCase().includes("SHORT") || displayType.toUpperCase().includes("SELL");

  const leverageMatch = displayType.match(/(\d+)/);
  const leverage = leverageMatch ? leverageMatch[1] : null;

  let badgeText = "";
  if (isLong) {
    badgeText = leverage ? `LONG (${leverage}X)` : "LONG";
  } else if (isShort) {
    badgeText = leverage ? `SHORT (${leverage}X)` : "SHORT";
  } else {
    badgeText = displayType;
  }

  const isDerivative = isLong || isShort;

  let displayPercent = percent || "";
  if (positive && !displayPercent.startsWith('+') && displayPercent !== '-') {
    displayPercent = '+' + displayPercent;
  }

  const getSymbol = (assetString: string) => {
    if (!assetString) return "btc";
    const base = assetString.split('/')[0].trim().toLowerCase();
    return base;
  };
  const symbol = getSymbol(asset);
  const logoUrl = `https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/${symbol}.png`;

  return (
    <tr className="hover:bg-neutral-800/10 transition-colors group cursor-pointer border-b border-[#1C1D26]/40 last:border-none">
      <td className="px-8 py-5">
        <div className="flex items-center gap-3">
          {!imgError ? (
            <img 
              src={logoUrl} 
              alt={asset} 
              className="w-8 h-8 rounded-full object-contain shrink-0"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs select-none shrink-0 ${
              asset.includes('BTC') 
                ? 'bg-gradient-to-tr from-[#F59E0B] to-[#D97706] text-black shadow-[0_0_12px_rgba(245,158,11,0.2)]' 
                : 'bg-gradient-to-tr from-[#8B5CF6] to-[#6D28D9] text-white shadow-[0_0_12px_rgba(139,92,246,0.2)]'
            }`}>
              {asset.includes('BTC') ? 'B' : asset.includes('ETH') ? 'Ξ' : asset[0]}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold text-white mb-0.5 tracking-tight font-sans">{asset}</p>
              {exchange && (
                <span className="text-[8px] font-bold bg-[#14151E] text-neutral-400 px-1.5 py-0.5 rounded border border-[#27272A]/40 uppercase tracking-widest font-sans select-none">
                  {exchange}
                </span>
              )}
            </div>
            {isDerivative ? (
              <span className={`text-[9px] font-extrabold tracking-wider font-sans ${isLong ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {badgeText}
              </span>
            ) : (
              <span className="text-[9px] font-extrabold text-neutral-500 uppercase tracking-wider font-sans">
                {badgeText}
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-8 py-5 text-xs font-semibold text-neutral-300 font-sans tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{entry}</td>
      <td className="px-8 py-5 text-xs font-semibold text-neutral-300 font-sans tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{size}</td>
      <td className="px-8 py-5">
        <p className={`text-xs font-bold ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'} mb-0.5 font-sans tracking-wide`} style={{ fontVariantNumeric: 'tabular-nums' }}>{pnl}</p>
        <p className={`text-[9px] font-semibold ${positive ? 'text-[#10B981]/80' : 'text-[#EF4444]/80'} tracking-widest font-sans`} style={{ fontVariantNumeric: 'tabular-nums' }}>{displayPercent}</p>
      </td>
      <td className="px-8 py-5 text-xs font-semibold text-neutral-400 font-sans tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{duration}</td>
      <td className="px-8 py-5 text-right">
        <button className="text-neutral-600 hover:text-white transition-colors"><MoreHorizontal size={18} /></button>
      </td>
    </tr>
  );
};

export default DashboardDemo;
